import { useMemo } from 'react'
import type { ItemType } from '../../types.ts'
import { escapeRegExp } from '../../utils/text-utils'
import { Combobox, type ComboboxProps } from './combobox'
import { InputContainer, type InputContainerProps } from './input-container'

/**
 * @description
 * While @see Combobox labels can be any React node, this component's labels
 * are always strings (to allow us to highlight matches within the string)
 */
type SearchableComboboxSelectOption = Omit<ItemType<ComboboxProps['options']>, 'label'> & {
  label: string
}

export type SearchableComboboxProps = InputContainerProps & {
  options: SearchableComboboxSelectOption[]
  filterByValue?: boolean
  searchValue?: ComboboxProps['value']
  setSearchValue?: ComboboxProps['setValue']
} & Pick<
    ComboboxProps,
    'placeholder' | 'isFetching' | 'open' | 'setOpen' | 'side' | 'avoidCollisions' | 'isClearable' | 'isDisabled'
  >

export const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  options,
  placeholder,
  filterByValue = false,
  searchValue,
  setSearchValue,
  isFetching,
  isClearable,
  isDisabled,
  open,
  setOpen,
  side,
  avoidCollisions,
  label,
  id,
  isRequired,
  explicitLabelCondition,
  helperText,
  externalClassName,
  errorText,
  isError,
}) => {
  const filteredOptions = useMemo(
    () =>
      options
        .filter(
          option => !filterByValue || !searchValue || option.value.toLowerCase().includes(searchValue.toLowerCase())
        )
        .map(({ label, value, enabled, checked, icon, metadata }) => ({
          label: <SelectOptionLabelWithHighlight label={label} searchValue={searchValue} />,
          value,
          enabled,
          checked,
          icon,
          metadata,
        })),
    [options, searchValue, filterByValue]
  )

  return (
    <InputContainer
      label={label}
      id={id}
      isRequired={isRequired}
      explicitLabelCondition={explicitLabelCondition}
      helperText={helperText}
      externalClassName={externalClassName}
      errorText={errorText}
      isError={isError}
    >
      <Combobox
        options={filteredOptions}
        value={searchValue}
        setValue={setSearchValue}
        placeholder={placeholder}
        isFetching={!!isFetching}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isError={isError || errorText !== undefined}
        open={open}
        setOpen={setOpen}
        side={side}
        avoidCollisions={avoidCollisions}
        id={id}
        aria-label={label}
      />
    </InputContainer>
  )
}

function SelectOptionLabelWithHighlight({
  label,
  searchValue,
}: {
  label: SearchableComboboxSelectOption['label']
  searchValue?: string
}) {
  const tokens = useTokens({ label, searchValue })

  return (
    <span>
      {tokens.map((token, index) => (
        <SelectOptionLabelToken key={index} {...token} />
      ))}
    </span>
  )
}

function SelectOptionLabelToken({ text, isHighlighted }: { text: string; isHighlighted: boolean }) {
  return isHighlighted ? (
    <span>
      <b>{text}</b>
    </span>
  ) : (
    <span>{text}</span>
  )
}

function useTokens({ label, searchValue }: Parameters<typeof tokenize>[0]) {
  return useMemo(() => tokenize({ label, searchValue }), [label, searchValue])
}

function tokenize({ label, searchValue }: { label: string; searchValue?: string }) {
  if (!searchValue) {
    return [{ text: label, isHighlighted: false }]
  }

  const regex = new RegExp(`(${escapeRegExp(searchValue)})`, 'gi')
  const tokens = [] as React.ComponentProps<typeof SelectOptionLabelToken>[]

  let match: RegExpExecArray | null = regex.exec(label)
  let currentIndex = 0
  while (match?.[0]) {
    // Add the text before the match
    tokens.push({
      text: label.slice(currentIndex, match.index),
      isHighlighted: false,
    })

    // Add the match
    tokens.push({
      text: match[0],
      isHighlighted: true,
    })

    currentIndex += match.index - currentIndex + match[0].length
    match = regex.exec(label)
  }

  // Add the text after the last match
  tokens.push({
    text: label.slice(currentIndex),
    isHighlighted: false,
  })

  return tokens
}
